import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import { IconAvatarProps } from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import { UserAvatarProps } from 'shared/components/vxl/avatar/UserAvatar/UserAvatar';
import AvatarItem from 'shared/components/vxl/AvatarCard/AvatarItem';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export interface AvatarCardChildProps extends VXLComponent {
  name: VXLComponentId.ICON_AVATAR | VXLComponentId.USER_AVATAR;
  props: IconAvatarProps | UserAvatarProps;
}

export interface AvatarWithLabelProps extends PressableAnalyticsProps {
  labelText: string;
  avatarProps: AvatarCardChildProps;
  onPress: () => void;
}

export interface AvatarCardProps extends PressableAnalyticsProps {
  /**The title to be displayed on the card. */
  titleText: string;
  /**On Press action to be perform on title press */
  titlePressAction: () => void;
  /** * array contains the avatar properties, label text, and a press handler. */
  avatarData: AvatarWithLabelProps[];
  /** Boolean prop to disable the user interaction */
  isDisabled: boolean;
}

const AvatarCard = ({
  titleText,
  avatarData,
  titlePressAction,
  isDisabled,
  analyticsId = undefined,
  customAttributes = null,
}: AvatarCardProps) => {
  const pressableAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  return (
    <View style={styles.outerContainer}>
      <Pressable
        onPress={() => {
          pressableAnalytics();
          titlePressAction();
        }}
        style={({ pressed, hovered, focused }) => [
          styles.headingContainer,
          hovered && styles.hovered,
          pressed && styles.pressed,
          focused && styles.focused,
          isDisabled && styles.disabledPointer,
        ]}
      >
        <BodySmBoldText
          style={isDisabled ? styles.disabledStyle : null}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {titleText}
        </BodySmBoldText>
        <View>
          <Icon
            iconKey="CHEVRON_RIGHT"
            iconSize={theme.spacing.m}
            iconColor={
              isDisabled ? theme.color.text.muted : theme.color.text.secondary
            }
          />
        </View>
      </Pressable>
      <AmplitudeMask>
        <View style={styles.bodyContainer}>
          {avatarData?.map((item) => (
            <AvatarItem
              key={item.avatarProps.id}
              item={item}
              isDisabled={isDisabled}
            />
          ))}
        </View>
      </AmplitudeMask>
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing.s,
  },
  disabledPointer: {
    pointerEvents: 'none',
  },
  disabledStyle: {
    color: theme.color.interactive.disabled.text,
  },
  focused: {
    outlineColor: theme.color.border.focused,
  },
  headingContainer: {
    alignItems: 'center',
    borderBottomWidth: 1,
    borderColor: theme.color.border.primary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },
  hovered: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  outerContainer: {
    backgroundColor: theme.color.interactive.secondary,
    borderRadius: 4,
    width: '100%',
  },
  pressed: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
});

export default AvatarCard;
